import { makeStyles } from '@material-ui/styles';
import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import RTLProvider from '../RTLProvider';
import Loading from '../components/Loading/Loading';
import Modal, { ErrorModal } from '../containers/Modal';
import PropTypes from '../helpers/propTypes';
import { CloseConfirmationProvider } from '../hooks/useCloseConfirmation';
import { GlobalStateProvider } from '../hooks/useGlobalState';
import useMessages from '../languages';
import { userMetrics } from '../userMetrics';
import { useAPI } from './service';

const useStyles = makeStyles({
  root: {
    '& *': {
      boxSizing: 'border-box',
      '-webkit-overflow-scrolling': 'touch',
      '-webkit-text-size-adjust': 'none',
      '-ms-text-size-adjust': 'none',
      'text-size-adjust': 'none',
    },
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,

    '@media print': {
      display: 'block',
      position: 'absolute',
      height: 'auto',
      bottom: 'auto',
    },

    '&:before': {
      content: '""',
      display: 'block',
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: '#1C1C1C',
      opacity: 0.42,
      zIndex: 0,

      '@media print': {
        display: 'none',
      },
    },
  },
});

function setDocumentTitle(msg) {
  if (msg) {
    document.title = msg['NavigationProgress.SignUp'];
  }
}

function App(props) {
  const { loading, errorCode, sessionInfo, sessionToken, config, onClose } =
    props;
  const { locale, language, messages, steps, studyId } = config;

  useEffect(() => {
    userMetrics.register({ studyId, locale });
    userMetrics.track('Prescreener opened');
  }, []);

  const [api, apiErrorCode] = useAPI(config, sessionToken);
  const { msg, msgLoading, msgErrorCode } = useMessages(locale, messages);
  const classes = useStyles();

  if (!apiErrorCode && !errorCode && !msgErrorCode && (loading || msgLoading)) {
    return (
      <div className={classes.root}>
        <Loading />
      </div>
    );
  }
  setDocumentTitle(msg);

  return (
    <IntlProvider locale={language?.substring(0, 3)} messages={msg}>
      <GlobalStateProvider initialKeys={steps}>
        <CloseConfirmationProvider>
          <div className={classes.root}>
            <RTLProvider>
              <Modal
                {...props}
                api={api}
                errorCode={apiErrorCode || errorCode || msgErrorCode}
                handleModalClose={() => onClose(props)}
                sessionInfo={sessionInfo}
              />
            </RTLProvider>
          </div>
        </CloseConfirmationProvider>
      </GlobalStateProvider>
    </IntlProvider>
  );
}

App.propTypes = {
  config: PropTypes.shape({
    baseURI: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    messages: PropTypes.shape({}),
    referrer: PropTypes.string,
    referrerToken: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    studyId: PropTypes.string.isRequired,
  }).isRequired,
  errorCode: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sessionInfo: PropTypes.shape({}),
  sessionToken: PropTypes.string,
};

App.defaultProps = {
  errorCode: undefined,
  sessionInfo: undefined,
  sessionToken: undefined,
};

export default App;

export function AppError({ config: { locale, language, messages }, onClose }) {
  const { msg } = useMessages(locale, messages);

  const classes = useStyles();

  const messagesWithFallback = {
    'Modal.UnknownError': 'An unknown error occurred',
    'Error.PleaseTryAgain': 'Please try again later',
    'Error.CloseModal': 'Close',
    ...msg,
  };

  return (
    <IntlProvider locale={language} messages={messagesWithFallback}>
      <div className={classes.root}>
        <ErrorModal handleModalClose={() => onClose()} />
      </div>
    </IntlProvider>
  );
}

AppError.propTypes = {
  config: PropTypes.shape({
    language: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    messages: PropTypes.shape({}),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
